import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
} from '@angular/core';
import {
  RadioCardComponent,
  RadioCardGroupComponent,
  TagComponent,
} from '@catch/general-ui';
import { ThemingService } from '@catch/services';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { StripeBillingInterval } from '../../../enums/stripe.enum';
import { StripeProduct } from '../../../interfaces/stripe';
import { translateBillingPeriod } from '../../../utils/billing-period-display.util';

@Component({
  selector: 'app-ats-addon-selector',
  imports: [
    CurrencyPipe,
    NgxSkeletonLoaderModule,
    RadioCardComponent,
    RadioCardGroupComponent,
    TagComponent,
    TranslocoPipe,
  ],
  templateUrl: './ats-addon-selector.component.html',
  styleUrl: './ats-addon-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AtsAddonSelectorComponent {
  readonly products = input.required<StripeProduct[]>();
  readonly loaded = input.required<boolean>();
  readonly billingInterval = input.required<StripeBillingInterval>();
  readonly disabled = input(false);
  readonly currentPriceId = input<string | null>(null);
  readonly unavailablePriceIds = input<readonly string[]>([]);
  readonly selectedPriceId = model<string | null>(null);

  readonly themingService = inject(ThemingService);
  private readonly transloco = inject(TranslocoService);
  readonly unavailablePrices = computed(
    () => new Set(this.unavailablePriceIds()),
  );

  /**
   * Resolves the display title for a catalog price.
   * @param {StripeProduct} product Catalog price
   * @returns {string}
   */
  productTitle(product: StripeProduct): string {
    return product.product?.name ?? product.id;
  }

  /**
   * Returns the billing-cycle display amount in major currency units.
   * @param {StripeProduct} product Catalog price
   * @returns {number}
   */
  monthlyDisplayAmount(product: StripeProduct): number {
    return product.unit_amount / 100;
  }

  /**
   * Translated billing-cycle label (e.g. "jedes Jahr" / "alle 4 Monate").
   * @param {StripeProduct} product Catalog price
   * @returns {string}
   */
  periodHint(product: StripeProduct): string {
    return translateBillingPeriod(
      this.transloco,
      product,
      this.billingInterval(),
    );
  }

  /**
   * Checks whether a product is the current ATS plan.
   * @param {StripeProduct} product Catalog price
   * @returns {boolean}
   */
  isCurrent(product: StripeProduct): boolean {
    return product.id === this.currentPriceId();
  }

  /**
   * Checks whether a product is unavailable for selection.
   * @param {StripeProduct} product Catalog price
   * @returns {boolean}
   */
  isUnavailable(product: StripeProduct): boolean {
    return this.unavailablePrices().has(product.id);
  }
}
